import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, ShoppingBag, Menu, X, User, ChevronDown } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import PincodeCheck from "@/components/PincodeCheck";
import MegaMenu from "@/components/MegaMenu";

const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function Navbar() {
  const { cartCount, wishlist, openDrawer, setSearchOpen, isAuthed, requireAuth, customer, logout, catalog } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedRoot, setExpandedRoot] = useState(null);
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => { setOpenMenu(null); setMenu(false); }, [location.pathname, location.search]);

  const keepOpen = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setOpenMenu(null), 220); };
  const openPanel = (key) => { keepOpen(); setOpenMenu(key); };

  const go = (path) => {
    setMenu(false);
    setOpenMenu(null);
    if (path.startsWith("/#")) {
      const id = path.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => import("@/lib/scroll").then((m) => m.scrollToId(id)), 350); }
      else import("@/lib/scroll").then((m) => m.scrollToId(id));
      return;
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    navigate(path);
  };

  const MENUS = [
    { key: "categories", label: "Categories" },
    { key: "brands", label: "Brands" },
  ];

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        onMouseLeave={scheduleClose}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled || openMenu ? "glass-dark border-b border-champagne/30" : ""} ${scrolled ? "py-2" : "py-4"}`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
          <nav className="hidden flex-1 items-center gap-6 lg:flex">
            <button data-testid="nav-link-home" onClick={() => go("/")} className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-sage transition-colors hover:text-cream">
              Home
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
            {MENUS.map((m) => (
              <button
                key={m.key}
                data-testid={`nav-link-${m.key}`}
                onMouseEnter={() => openPanel(m.key)}
                onClick={() => setOpenMenu(openMenu === m.key ? null : m.key)}
                aria-expanded={openMenu === m.key}
                className={`group relative flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors hover:text-cream ${openMenu === m.key ? "text-gold" : "text-sage"}`}
              >
                {m.label}
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${openMenu === m.key ? "rotate-180 text-gold" : ""}`} />
                <span className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 ${openMenu === m.key ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
            <button data-testid="nav-link-offers" onClick={() => go("/offers")} className="group relative font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-gold transition-colors hover:text-cream">
              Offers
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
            <button data-testid="nav-link-branches" onClick={() => go("/stores")} className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-sage transition-colors hover:text-cream">
              Our Branches
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          </nav>

          <button data-testid="nav-menu-button" onClick={() => setMenu(true)} className="flex h-10 w-10 items-center justify-center rounded-full border border-champagne/40 text-cream lg:hidden" aria-label="Open menu">
            <Menu className="h-4 w-4" />
          </button>

          <Link to="/" data-testid="nav-logo" className="absolute left-1/2 -translate-x-1/2 px-2 py-1 transition-transform duration-300 hover:scale-105" aria-label="Soonyoung home">
            <img src="/assets/soonyoung-logo-trim.png" alt="Soonyoung" className="h-8 w-auto mix-blend-multiply sm:h-9" />
          </Link>

          <div className="flex flex-1 items-center justify-end gap-2 sm:gap-3">
            <PincodeCheck />
            <button data-testid="nav-search-button" onClick={() => setSearchOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-full border border-champagne/40 text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Search">
              <Search className="h-4 w-4" />
            </button>
            <button data-testid="nav-wishlist-button" onClick={() => requireAuth(() => openDrawer("saved"))} className="relative hidden h-10 w-10 items-center justify-center rounded-full border border-champagne/40 text-cream transition-colors hover:border-gold hover:text-gold sm:flex" aria-label="Saved rituals">
              <Heart className="h-4 w-4" />
              {wishlist.ids.length > 0 && (
                <span data-testid="wishlist-count-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[9px] font-bold text-forest">{wishlist.ids.length}</span>
              )}
            </button>
            <button data-testid="nav-account-button" onClick={() => requireAuth(() => go("/account"))} className="hidden h-10 w-10 items-center justify-center rounded-full border border-champagne/40 text-cream transition-colors hover:border-gold hover:text-gold sm:flex" aria-label="Account">
              <User className={`h-4 w-4 ${isAuthed ? "text-gold" : ""}`} />
            </button>
            <button data-testid="nav-cart-button" onClick={() => requireAuth(() => openDrawer("bag"))} className="relative flex h-10 w-10 items-center justify-center rounded-full bg-cream text-forest transition-colors hover:bg-gold" aria-label="Shopping bag">
              <ShoppingBag className="h-4 w-4" />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[9px] font-bold text-forest">{cartCount}</span>
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {openMenu && (
            <MegaMenu
              key={openMenu}
              type={openMenu}
              catalog={catalog}
              onNavigate={go}
              onKeepOpen={keepOpen}
              onScheduleClose={scheduleClose}
            />
          )}
        </AnimatePresence>
      </motion.header>

      <AnimatePresence>
        {menu && (
          <motion.div data-testid="mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-forest/95 p-6 backdrop-blur-xl">
            <div className="flex items-center justify-between">
              <img src="/assets/soonyoung-logo-trim.png" alt="Soonyoung" className="h-7 w-auto mix-blend-multiply" />
              <button data-testid="mobile-menu-close" onClick={() => setMenu(false)} className="flex h-10 w-10 items-center justify-center rounded-full border border-champagne/40 text-cream" aria-label="Close menu">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-10 flex flex-col">
              <button data-testid="mobile-nav-home" onClick={() => go("/")} className="border-b border-champagne/30 py-4 text-left font-display text-4xl text-cream hover:text-gold">Home</button>
              <button data-testid="mobile-nav-offers" onClick={() => go("/offers")} className="border-b border-champagne/30 py-4 text-left font-display text-4xl text-gold hover:text-cream">Offers</button>
              {MENUS.map((m) => (
                <div key={m.key} className="border-b border-champagne/30">
                  <button
                    data-testid={`mobile-nav-${m.key}`}
                    onClick={() => setExpanded(expanded === m.key ? null : m.key)}
                    aria-expanded={expanded === m.key}
                    className="flex w-full items-center justify-between py-4 text-left font-display text-4xl text-cream hover:text-gold"
                  >
                    {m.label}
                    <ChevronDown className={`h-6 w-6 text-gold transition-transform duration-300 ${expanded === m.key ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {expanded === m.key && (
                      <motion.div
                        data-testid={`mobile-submenu-${m.key}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-1 pb-5">
                          {m.key === "categories" ? (
                            <>
                              {(catalog.categories || []).filter((c) => !c.parent_id).map((root) => {
                                const subs = (catalog.categories || []).filter((c) => c.parent_id === root.id);
                                const isOpen = expandedRoot === root.id;
                                return (
                                  <div key={root.id}>
                                    <button
                                      data-testid={`mobile-root-${root.id}`}
                                      onClick={() => setExpandedRoot(isOpen ? null : root.id)}
                                      aria-expanded={isOpen}
                                      className={`flex w-full items-center justify-between py-2 pl-4 text-left font-display text-xl transition-colors ${isOpen ? "text-gold" : "text-sage hover:text-gold"}`}
                                    >
                                      {titleCase(root.name)}
                                      <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? "rotate-180 text-gold" : "text-fern"}`} />
                                    </button>
                                    <AnimatePresence>
                                      {isOpen && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                          <div className="flex flex-col pb-2 pl-8">
                                            <button data-testid={`mobile-sub-all-${root.id}`} onClick={() => go(`/category/${root.id}`)} className="py-1.5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                                              All {titleCase(root.name)} →
                                            </button>
                                            {subs.map((s) => (
                                              <button key={s.id} data-testid={`mobile-categories-${s.id}`} onClick={() => go(`/category/${s.id}`)} className="flex items-center justify-between py-1.5 text-left font-display text-lg text-fern hover:text-gold">
                                                {titleCase(s.name)}
                                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-fern">{s.product_count ? `${s.product_count}` : "soon"}</span>
                                              </button>
                                            ))}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            (catalog.brands || []).slice(0, 12).map((it) => (
                              <button key={it.id} data-testid={`mobile-brands-${it.id}`} onClick={() => go(`/shop?brand_id=${it.id}`)} className="flex items-center justify-between py-2 pl-4 text-left font-display text-xl text-sage hover:text-gold">
                                {titleCase(it.name)}
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-fern">{it.product_count ? `${it.product_count}` : "soon"}</span>
                              </button>
                            ))
                          )}
                          <button data-testid={`mobile-${m.key}-all`} onClick={() => go(m.key === "categories" ? "/shop" : "/#brands")} className="py-2 pl-4 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                            View all →
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <button data-testid="mobile-nav-shop" onClick={() => go("/shop")} className="border-b border-champagne/30 py-4 text-left font-display text-4xl text-cream hover:text-gold">Shop All</button>
              <button data-testid="mobile-nav-branches" onClick={() => go("/stores")} className="border-b border-champagne/30 py-4 text-left font-display text-4xl text-cream hover:text-gold">Our Branches</button>
            </div>
            <div className="mt-auto pt-8">
              {isAuthed ? (
                <div className="flex items-center justify-between">
                  <button data-testid="mobile-nav-account" onClick={() => go("/account")} className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{customer?.name || customer?.mobile}</button>
                  <button data-testid="mobile-nav-logout" onClick={() => { logout(); setMenu(false); }} className="font-mono text-[10px] uppercase tracking-[0.25em] text-fern">Sign out</button>
                </div>
              ) : (
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fern">Botanical Cosmetic Showroom — Est. 2026</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
